<template>
  <Content>
    <Block center>
      <header>
        <h1>{{ $t('demographics_header') }}</h1>
        <div>
          {{ $t('demographics_prompt') }}

          <Dialog v-model="dialog" v-bind="$props">
            <template v-slot:activator="{ on, attrs }">
              <a href="#" v-bind="attrs" v-on="on">{{
                $t('demographics_why_link')
              }}</a>
            </template>
            <Card>
              <CardHeader>
                <CardTitle>{{
                  $t('more_information_demographics_why_header')
                }}</CardTitle>
              </CardHeader>
              <CardBody>
                <CardText>{{
                  $t('more_information_demographics_why_detail')
                }}</CardText>
                <CardActions>
                  <Button :label="$t('button_close')" @click="dialog = false" />
                </CardActions>
              </CardBody>
            </Card>
          </Dialog>
        </div>
      </header>
      <Form v-model="valid" ref="form">
        <Row>
          <Column>
            <TextInput
              required
              v-model="user.given_name"
              :label="$t('first_name_label')"
              :placeholder="$t('register_firstname_placeholder')"
            />
          </Column>

          <Column>
            <TextInput
              required
              v-model="user.family_name"
              :label="$t('last_name_label')"
              :placeholder="$t('register_lastname_placeholder')"
            />
          </Column>
        </Row>

        <Row>
          <Column>
            <TextInput
              required
              :rules="[validDate, validAge]"
              :mask-options="maskOptions"
              placeholder="YYYY-MM-DD"
              :messages="formattedDate"
              v-model="user.date_of_birth"
              :label="$t('demographics_birthday_field')"
            />
          </Column>

          <Column>
            <Select
              required
              :options="this.sexes"
              v-model="user.sex"
              :label="$t('demographics_biological_sex_field')"
            />
          </Column>
        </Row>
        <Row>
          <Column>
            <Select
              required
              :options="races"
              v-model="user.ethnicity"
              :label="$t('demographics_race_ethnicity_field')"
            />
          </Column>
        </Row>

        <Row>
          <Column>
            <Select
              required
              :options="this.education"
              v-model="user.education"
              :label="$t('demographics_education_field')"
            />
          </Column>
        </Row>

        <BlockActions>
          <Button
            :disabled="!isDirty || !valid"
            class="submit"
            :loading="saving"
            :label="$t('button_save')"
            @click="updateProfile"
          />
          <Button
            v-if="!required"
            to="/"
            type="tertiary"
            :label="$t('button_back_to_assessments')"
          />
        </BlockActions>
      </Form>
    </Block>
  </Content>
</template>

<script>
import {
  Block,
  BlockActions,
  Content,
  Form,
  Select,
  Button,
  TextInput,
  Row,
  Col as Column,
  Dialog,
  Card,
  CardTitle,
  CardText,
  CardActions,
  CardBody,
  CardHeader
} from '@neurotrack/web-shared-components'

import { EDUCATION_LEVELS, RACES, SEXES } from '@/constants'
import { mapMutations, mapState, mapActions } from 'vuex'
import { showModal } from '@/store'
import isEqual from 'lodash-es/isEqual'
import { ntDate } from '@neurotrack/web-core'

export default {
  name: 'Profile',
  components: {
    Button,
    Form,
    Select,
    TextInput,
    Block,
    BlockActions,
    Row,
    Column,
    Content,
    Dialog,
    Card,
    CardTitle,
    CardText,
    CardActions,
    CardBody,
    CardHeader
  },
  props: {
    required: {
      type: Boolean,
      default: false
    },
    redirect: {
      type: String
    }
  },
  data() {
    return {
      dialog: false,
      valid: true,
      user: undefined,
      saving: false,
      maskOptions: {
        date: true,
        delimiter: '-',
        datePattern: ['Y', 'm', 'd']
      }
    }
  },
  watch: {
    profile: {
      handler(val) {
        this.cloneUser(val)
      },
      immediate: true
    }
  },
  computed: {
    ...mapState('user', ['profile']),
    isDirty() {
      return !isEqual(this.user, this.profile)
    },
    formattedDate() {
      const date = this.user?.date_of_birth
      if (this.validDate(date) === true && this.validAge(date) === true) {
        /**
         * We want to make sure this doesn't auto-append the current time
         */
        return ntDate.formatDate(`${date} 0:00`)
      }
      return ''
    },
    races() {
      return RACES.map(race => ({
        ...race,
        label: this.$i18n.t(race.translationKey)
      }))
    },
    education() {
      return EDUCATION_LEVELS.map(level => ({
        ...level,
        label: this.$i18n.t(level.translationKey)
      }))
    },
    sexes() {
      return SEXES.map(sex => ({
        ...sex,
        label: this.$i18n.t(sex.translationKey)
      }))
    }
  },
  methods: {
    ...mapMutations([showModal]),
    ...mapActions('user', ['logout']),
    ...mapActions('user', ['saveProfile']),
    cloneUser(profile) {
      /** Make an object clone for later comparison */
      this.user = JSON.parse(JSON.stringify(profile || this.profile))
    },
    validDate(value) {
      return value && ntDate.isFormattedDate(value)
        ? true
        : this.$t('dob_compliance_error')
    },
    validAge(value) {
      if (!value) {
        return true
      }
      const today = new Date()
      const dateMin = new Date(value)
      dateMin.setFullYear(dateMin.getFullYear() + 18)
      const dateMax = new Date(value)
      dateMax.setFullYear(dateMax.getFullYear() + 120)
      return value && today > dateMin && today < dateMax ? true : this.$t('dob_compliance_error')
    },
    async updateProfile() {
      this.saving = true
      await this.saveProfile(this.user)
      this.saving = false
      const path = this.redirect || '/'
      this.$router.push({ path })
    }
  }
}
</script>
